import React, { FC, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { AppOffer, AppOfferListItem, AppPersonalizedOfferGroup } from 'state/loyalty/types';
import noop from 'utils/noop';

import { useEnableLoyaltyServiceOffers } from '../hooks/use-enable-loyalty-service-offers';
import {
  UseLoyaltyServiceAppliedOffersResult,
  useLoyaltyServiceAppliedOffers,
} from '../hooks/use-loyalty-service-applied-offers';
import { useLoyaltyServiceOffers } from '../hooks/use-loyalty-service-offers';

/**
 * The ON ("loyalty-service") service-offers domain interface consumed by the
 * Offers page and applied-offer consumers.
 */
export interface ServiceOffersContextValue {
  offerListItems: AppOfferListItem[];
  offersInCart: AppOffer[];
  personalizedOfferGroups: AppPersonalizedOfferGroup[];
  loading: boolean;
  baseOffersLoading: boolean;
  error?: Error;
  hasSettled: boolean;
  isRefreshing: boolean;
  blockers: ReadonlyArray<'authentication' | 'offer-query'>;
  storeId?: string;
  serviceMode?: string;
  refreshOffers: () => void;
  reprocessOffers: () => void;
  /**
   * The applied-offers batch recovery cluster: `ready` (eviction readiness
   * gate), `data` (metadata + present-offer membership + the variables they were
   * settled against, or null until ready), `lastSettledMetadataById` (the last
   * settle's metadata, held across the null-on-refetch windows for display
   * continuity), and `refresh`. Eviction reads `data.presentOfferIds` (fresh
   * membership). Both signed-in and guest sessions recover their applied
   * offers through this batch, so recovery no longer depends on the older
   * fallback path.
   */
  appliedOffersBatch: UseLoyaltyServiceAppliedOffersResult;
  /**
   * Browse-scope activation. The provider is mounted app-wide, but the
   * paginated browse-list query must only run while the Offers list is
   * mounted. Consumers call `useEnableServiceOffersBrowse()` rather than these
   * directly.
   */
  activateBrowse: () => void;
  deactivateBrowse: () => void;
}

// Safe default so a consumer rendered outside the provider (e.g. OFF path or a
// route where the provider is not mounted) degrades gracefully rather than
// throwing.
const defaultServiceOffersContextValue: ServiceOffersContextValue = {
  offerListItems: [],
  offersInCart: [],
  personalizedOfferGroups: [],
  loading: false,
  baseOffersLoading: false,
  hasSettled: false,
  isRefreshing: false,
  blockers: [],
  refreshOffers: noop,
  reprocessOffers: noop,
  appliedOffersBatch: {
    ready: false,
    data: null,
    lastSettledMetadataById: {},
    retainAppliedOffer: noop,
    clearRetainedMetadata: noop,
    refresh: noop,
  },
  activateBrowse: noop,
  deactivateBrowse: noop,
};

export const ServiceOffersContext = createContext<ServiceOffersContextValue>(
  defaultServiceOffersContextValue
);

export const useServiceOffers = () => useContext(ServiceOffersContext);

/**
 * Enables the browse-list query for as long as the calling component is
 * mounted. The Offers list calls this so the paginated `network-only` query
 * only runs there, not everywhere the app-wide provider is mounted.
 */
export const useEnableServiceOffersBrowse = () => {
  const { activateBrowse, deactivateBrowse } = useServiceOffers();

  useEffect(() => {
    activateBrowse();
    return deactivateBrowse;
  }, [activateBrowse, deactivateBrowse]);
};

export const ServiceOffersProvider: FC<IBaseProps> = ({ children }) => {
  const { enabled } = useEnableLoyaltyServiceOffers();

  // Gate via a prop, never by branching the element type: swapping Fragment vs
  // provider on a gate change would remount the whole downstream app subtree
  // (router, OrderProvider, …), which `src/providers.ts` warns against.
  return <ServiceOffersProviderInner enabled={enabled}>{children}</ServiceOffersProviderInner>;
};

const ServiceOffersProviderInner: FC<IBaseProps & { enabled: boolean }> = ({
  enabled,
  children,
}) => {
  // Ref-counted so overlapping consumers (e.g. list + a transient reader) keep
  // the browse query alive until the last one unmounts.
  const [browseActiveCount, setBrowseActiveCount] = useState(0);
  const activateBrowse = useCallback(() => setBrowseActiveCount(count => count + 1), []);
  const deactivateBrowse = useCallback(
    () => setBrowseActiveCount(count => Math.max(0, count - 1)),
    []
  );
  const browseEnabled = enabled && browseActiveCount > 0;

  const {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    loading: browseLoading,
    refreshOffers,
    reprocessOffers,
    error,
    hasSettled,
    isRefreshing,
    blockers,
    storeId,
    serviceMode,
  } = useLoyaltyServiceOffers({ enabled: browseEnabled });

  // Runs app-wide (not gated on the browse list) so cold-start recovery happens
  // without the Offers screen mounting.
  const appliedOffersBatch = useLoyaltyServiceAppliedOffers({ enabled });

  // While the list is mounted but the browse query has not been enabled/resolved
  // yet, report loading so the Offers screen shows its skeleton instead of a
  // one-frame flash of the empty state.
  const loading = !browseEnabled || browseLoading;

  // When OFF, expose the inert default so consumers fall back to `cmsOffers` via
  // the metadata seam (the hooks above still run but skip on `enabled`).
  const value: ServiceOffersContextValue = enabled
    ? {
        offerListItems,
        offersInCart,
        personalizedOfferGroups,
        loading,
        baseOffersLoading: loading,
        refreshOffers,
        reprocessOffers,
        error,
        hasSettled,
        isRefreshing,
        blockers,
        storeId,
        serviceMode,
        appliedOffersBatch,
        activateBrowse,
        deactivateBrowse,
      }
    : defaultServiceOffersContextValue;

  return <ServiceOffersContext.Provider value={value}>{children}</ServiceOffersContext.Provider>;
};
